import React, { useState } from 'react';
import { FaSave } from 'react-icons/fa';
import {
  Box,
  Button,
  Textarea,
  FormControl,
  FormLabel,
  useToast,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure
} from '@chakra-ui/react';
import { s3Service } from '../services/api';

function EnvEditor({ fileKey, initialContent, onUpdate, readOnly, showRawOnly = false }) {
  const [content, setContent] = useState(initialContent);
  const [saving, setSaving] = useState(false);
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();

  const handleConfirmOpen = () => {
    if (readOnly) {
      toast({
        title: "Permission denied",
        description: "You don't have permission to edit this file.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
      return;
    }
    
    // Open the confirmation dialog
    onOpen();
  };
  
  const handleUpdate = async () => {
    onClose(); // Close the dialog
    
    try {
      setSaving(true);
      await s3Service.updateEnvFile(fileKey, content);
      
      toast({
        title: "File updated",
        description: "The .env file has been updated successfully.",
        status: "success",
        duration: 3000,
        isClosable: true
      });
      
      if (onUpdate) onUpdate(content);
    } catch (error) {
      console.error("Error updating file:", error);
      toast({
        title: "Update failed",
        description: "There was an error updating the file.",
        status: "error",
        duration: 5000,
        isClosable: true
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box mt={3}>
      <FormControl>
        <FormLabel fontWeight="bold" fontSize="sm">
          {showRawOnly ? (fileKey.endsWith('.php') ? 'PHP Source Code' : 
                          fileKey.endsWith('.htaccess') ? '.htaccess Content' : 
                          'File Content') : 
           'Raw .env Content'}
        </FormLabel>
        <Textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          height={showRawOnly ? "600px" : "400px"}
          fontFamily="monospace"
          size="sm"
          fontSize="sm"
          isReadOnly={readOnly}
          color="inherit"
          backgroundColor="#fff"
          _dark={{ borderColor: 'gray.600' }}
        />
      </FormControl>
      
      {!readOnly && (
        <Flex justify="flex-start" mt={2}>
          <Button
            colorScheme="teal"
            onClick={handleConfirmOpen}
            isLoading={saving}
            loadingText="Saving"
            size="sm"
            leftIcon={<FaSave />}
          >
            Save Changes
          </Button>
        </Flex>
      )}
      
      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Save Environment File
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to save changes to this .env file?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                No
              </Button>
              <Button colorScheme="teal" onClick={handleUpdate} ml={3} isLoading={saving}>
                Yes
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default EnvEditor;
